import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Breadcrumb,
  Button,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Typography,
  message,
} from 'antd';
import {
  ExclamationCircleOutlined,
  HomeOutlined,
  MobileOutlined,
} from '@ant-design/icons';
import SEO from './SEO';
import {
  notificationTimeout,
  notificationType,
} from '../common/constants';
import {
  firGetGlobalVersionGate,
  firUpdateGlobalVersionGate,
  primaryAuth,
} from '../firebase';
import useAuthClaims from '../common/useAuthClaims';
import './style.scss';

const { Text } = Typography;

// rev 95 (2026-05-17) — Mobile Version Gate tab. Root-only (the route is
// only mounted when claims.root is true; this guard is defence-in-depth).
//
// Edits a single project-wide doc `config/global.minSupportedAppVersion`
// rather than per-site, because the same App Store / Play Store binary
// serves every site. Bumping these values BLOCKS every install on a
// lower version the next time it launches.
//
// Type-to-confirm phrase is `BUMP MOBILE VERSION GATE`. Save disabled
// until typed exactly. Pairs with the rev 95 `v2auditConfigChanges`
// trigger which emits a `global_version_gate_bump` audit row.
export default function MobileVersionGate() {
  const claims = useAuthClaims();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState({ ios: '', android: '', ipad: '' });
  const [form] = Form.useForm();
  const [confirmPhrase, setConfirmPhrase] = useState('');

  const [messageApi, contextHolder] = message.useMessage();
  const { timeoutError, timeoutSuccess } = notificationTimeout;
  const { typeError, typeSuccess } = notificationType;
  const openMessage = useCallback(
    (content, duration, type) => messageApi.open({ content, duration, type }),
    [messageApi],
  );

  const REQUIRED_PHRASE = 'BUMP MOBILE VERSION GATE';

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const gate = await firGetGlobalVersionGate();
      setCurrent(gate);
      form.setFieldsValue({
        ios:     gate.ios     || '1.0.0',
        android: gate.android || '1.0.0',
        ipad:    gate.ipad    || '1.0.0',
      });
    } catch (e) {
      console.error('-- Error loading global version gate:', e);
      openMessage('Error loading version gate', timeoutError, typeError);
    } finally {
      setLoading(false);
    }
  }, [form, openMessage, timeoutError, typeError]);

  useEffect(() => {
    if (claims.ready && claims.root) refresh();
  }, [claims.ready, claims.root, refresh]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const ios     = (values.ios     || '').trim();
      const android = (values.android || '').trim();
      const ipad    = (values.ipad    || '').trim();

      if (confirmPhrase !== REQUIRED_PHRASE) {
        openMessage('Confirm phrase does not match', timeoutError, typeError);
        return;
      }

      const byUid = primaryAuth.currentUser?.uid;
      if (!byUid) {
        openMessage('Sign-in expired — refresh and try again', timeoutError, typeError);
        return;
      }

      setSaving(true);
      await firUpdateGlobalVersionGate({ ios, android, ipad }, byUid);
      openMessage('Mobile version gate updated', timeoutSuccess, typeSuccess);
      setConfirmPhrase('');
      refresh();
    } catch (e) {
      if (e.errorFields) return;
      console.error('-- Error updating global version gate:', e);
      openMessage(`Error: ${e.message}`, timeoutError, typeError);
    } finally {
      setSaving(false);
    }
  };

  if (!claims.ready) {
    return (
      <>
        <SEO description="Mobile Version Gate" name="qiwei" title="Mobile Version Gate" type="webapp" />
        <Text type="secondary">Loading…</Text>
      </>
    );
  }
  if (!claims.root) {
    return (
      <>
        <SEO description="Mobile Version Gate" name="qiwei" title="Mobile Version Gate" type="webapp" />
        <Alert
          message="Staff-only"
          description="The Mobile Version Gate is restricted to ATACT staff."
          showIcon
          type="warning"
        />
      </>
    );
  }

  return (
    <>
      <SEO
        description="Project-wide mobile force-upgrade gate"
        name="qiwei"
        title="Mobile Version Gate"
        type="webapp"
      />
      {contextHolder}
      <Breadcrumb
        items={[
          {
            title: (
              <Link to="..">
                <HomeOutlined className="dropdown-icon-margin" />
                <span>Home</span>
              </Link>
            ),
          },
          { title: <span>Mobile Version Gate</span> },
        ]}
      />

      <Alert
        icon={<ExclamationCircleOutlined />}
        showIcon
        type="warning"
        message="Project-wide gate. Bumping these values BLOCKS every install below the new floor."
        description={
          <div>
            <p style={{ marginBottom: 6 }}>
              The same App Store / Play Store binary serves every site, so this gate is global, not
              per-site. Mobile apps read{' '}
              <code>config/global.minSupportedAppVersion</code> on launch and force-update if the
              installed bundle version is below the value for that platform. There is no in-app
              dismiss.
            </p>
            <p style={{ marginBottom: 0 }}>
              Confirm the target version is already published on BOTH App Store and Play Store
              before saving. If you bump to a version that doesn&apos;t exist yet, every user is
              locked out until you ship it (or revert).
            </p>
          </div>
        }
        style={{ margin: '12px 0 16px' }}
      />

      <div style={{
        background: '#fafafa',
        border: '1px solid #d9d9d9',
        borderRadius: 6,
        padding: '12px 16px',
        marginBottom: 16,
      }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>
          <MobileOutlined style={{ marginRight: 6 }} />
          Current floor
        </div>
        <div style={{ fontFamily: 'monospace', color: '#595959' }}>
          iOS: {current.ios || '(unset)'} · Android: {current.android || '(unset)'} · iPad: {current.ipad || '(unset)'}
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        disabled={loading || saving}
      >
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              label="iOS minimum"
              name="ios"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Input placeholder="e.g., 1.4.0" maxLength={20} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Android minimum"
              name="android"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Input placeholder="e.g., 1.4.0" maxLength={20} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="iPad minimum"
              name="ipad"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Input placeholder="e.g., 1.4.0" maxLength={20} />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: '8px 0 12px' }} />

        <div style={{ marginBottom: 8 }}>
          <strong>Type to confirm:</strong>
          <div style={{ fontFamily: 'monospace', color: '#cf1322', marginTop: 4 }}>
            {REQUIRED_PHRASE}
          </div>
        </div>
        <Input
          placeholder="Type the phrase above exactly to enable Save"
          value={confirmPhrase}
          onChange={(e) => setConfirmPhrase(e.target.value)}
          style={{
            fontFamily: 'monospace',
            borderColor: confirmPhrase === REQUIRED_PHRASE ? '#52c41a' : undefined,
            marginBottom: 16,
          }}
        />

        <Button
          danger
          loading={saving}
          disabled={confirmPhrase !== REQUIRED_PHRASE || loading}
          onClick={handleSave}
          size="large"
          type="primary"
        >
          Save mobile version gate
        </Button>
      </Form>
    </>
  );
}
